import { Component, OnInit } from '@angular/core';
import { DataService } from '../services/data.service';
import { MessageService } from 'primeng/api';

const TEAMS = ['Content', 'CMS', 'Backend', 'Client', 'Art'];

@Component({
  selector: 'app-teams',
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.sass'],
})
export class TeamComponent implements OnInit {
  constructor(
    private dataService: DataService,
    private messageService: MessageService,
  ) {}

  public teamGroups: { name: string; members: any[] }[] = [];
  public teamOptions = TEAMS.map(t => ({ label: t, value: t }));
  public loading = true;
  public savingUserId: string | null = null;

  // Modal state
  public modalVisible = false;
  public modalTeam: string = '';
  public modalUserOptions: { label: string; value: any }[] = [];
  public selectedUsers: any[] = [];
  public modalSaving = false;
  private allUsers: any[] = [];

  async ngOnInit(): Promise<void> {
    await this.loadUsers();
  }

  async loadUsers(): Promise<void> {
    this.loading = true;
    try {
      const users = await this.dataService.getAllOfTypeAsync('users', {
        autopopulate: false,
        virtuals: false,
        query: {},
        sort: { name: 1 },
      });

      this.allUsers = users || [];
      this.rebuildGroups();
    } finally {
      this.loading = false;
    }
  }

  private rebuildGroups(): void {
    const grouped = new Map<string, any[]>();
    for (const team of TEAMS) grouped.set(team, []);

    for (const user of this.allUsers) {
      const key = TEAMS.includes(user.team) ? user.team : null;
      if (key) grouped.get(key)!.push(user);
    }

    this.teamGroups = TEAMS.map(name => ({ name, members: grouped.get(name)! }));
  }

  // fromTeam comes from the accordion group — safe even after ngModel mutates user.team
  async onTeamChange(user: any, fromTeam: string, newTeam: string): Promise<void> {
    if (fromTeam === newTeam) return;
    this.savingUserId = user._id;
    try {
      await this.dataService.updateRecord(user.id, 'users', { team: newTeam });
      user.team = newTeam;
      this.regroupUser(user, fromTeam, newTeam);
      this.messageService.add({
        severity: 'success',
        summary: 'Team updated',
        detail: `${user.name || user.email} moved to ${newTeam}`,
      });
    } catch {
      user.team = fromTeam;
      this.messageService.add({
        severity: 'error',
        summary: 'Update failed',
        detail: `Could not update team for ${user.name || user.email}`,
      });
    } finally {
      this.savingUserId = null;
    }
  }

  openAddModal(teamName: string, event: Event): void {
    event.stopPropagation();
    this.modalTeam = teamName;
    this.selectedUsers = [];
    this.refreshModalOptions();
    this.modalVisible = true;
  }

  onModalTeamChange(): void {
    this.selectedUsers = [];
    this.refreshModalOptions();
  }

  private refreshModalOptions(): void {
    const currentMembers = new Set(
      (this.teamGroups.find(g => g.name === this.modalTeam)?.members || []).map((m: any) => m._id),
    );
    this.modalUserOptions = this.allUsers
      .filter(u => !currentMembers.has(u._id))
      .map(u => ({
        label: u.name && u.name !== u.email ? `${u.name} (${u.email})` : u.email,
        value: u,
      }));
  }

  async saveModal(): Promise<void> {
    if (!this.selectedUsers.length || !this.modalTeam) return;
    this.modalSaving = true;
    try {
      await Promise.all(
        this.selectedUsers.map(user =>
          this.dataService.updateRecord(user.id, 'users', { team: this.modalTeam }),
        ),
      );

      for (const user of this.selectedUsers) {
        const prevTeam = user.team;
        user.team = this.modalTeam;
        this.regroupUser(user, prevTeam, this.modalTeam);
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Members added',
        detail: `${this.selectedUsers.length} user${this.selectedUsers.length !== 1 ? 's' : ''} added to ${this.modalTeam}`,
      });
      this.modalVisible = false;
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Update failed',
        detail: 'One or more users could not be updated.',
      });
    } finally {
      this.modalSaving = false;
    }
  }

  private regroupUser(user: any, fromTeam: string, toTeam: string): void {
    const fromGroup = this.teamGroups.find(g => g.name === fromTeam);
    if (fromGroup) {
      fromGroup.members = fromGroup.members.filter(m => m._id !== user._id);
    }
    const toGroup = this.teamGroups.find(g => g.name === toTeam);
    if (toGroup) {
      toGroup.members = [...toGroup.members, user].sort((a, b) =>
        (a.name || a.email || '').localeCompare(b.name || b.email || ''),
      );
    }
  }
}
